/**
 * ConcessionsReport — Phase 19D
 * Per-student concession breakdown for the active session. Filter by reason
 * and download the RTE-inspector-friendly CSV.
 */
import React, { useEffect, useMemo, useState } from "react";
import { DownloadSimple, ShieldCheck } from "@phosphor-icons/react";
import api, { API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const REASON_OPTS = [
  { v: "__all__", label: "All reasons" },
  { v: "rte", label: "RTE" },
  { v: "sibling", label: "Sibling discount" },
  { v: "staff_child", label: "Staff child" },
  { v: "scholarship", label: "Scholarship" },
  { v: "merit", label: "Merit" },
  { v: "negotiated", label: "Negotiated" },
  { v: "other", label: "Other" },
];

export default function ConcessionsReport() {
  const [reason, setReason] = useState("__all__");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = reason !== "__all__" ? `?reason_code=${reason}` : "";
      const r = await api.get(`/school/reports/concessions${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason]);

  const downloadCsv = () => {
    const tok = tokenStore.get();
    const q = reason !== "__all__" ? `?reason_code=${reason}` : "";
    fetch(`${API_BASE}/school/reports/exports/concessions.csv${q}`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        return r.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `concessions${reason !== "__all__" ? "-" + reason : ""}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const items = data?.items || [];
  const totals = data?.totals || { students: 0, annual_waiver: 0 };

  return (
    <div className="space-y-6" data-testid="concessions-report-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl flex items-center gap-2">
            <ShieldCheck size={22} weight="duotone" /> Concessions report
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Per-student concession breakdown for the active session. Download
            CSV for RTE inspections or accounting audits.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Reason</div>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="w-48" data-testid="reason-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REASON_OPTS.map((r) => (
                  <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={downloadCsv} variant="outline" className="gap-1.5" data-testid="export-csv-btn">
            <DownloadSimple size={14} /> CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Kpi label="Students with concessions" value={totals.students} testid="kpi-students" />
        <Kpi
          label="Annual waiver value"
          value={formatINR(totals.annual_waiver)}
          tone="amber"
          testid="kpi-annual-waiver"
        />
        <Kpi label="Active reason filter" value={REASON_OPTS.find((r) => r.v === reason)?.label || "All"} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Adm No</th>
                <th className="px-3 py-2 text-left">Student</th>
                <th className="px-3 py-2 text-left">Class</th>
                <th className="px-3 py-2 text-left">Parent</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-right">Annual base</th>
                <th className="px-3 py-2 text-right">Effective</th>
                <th className="px-3 py-2 text-right">Waiver</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500" data-testid="no-concessions-report">
                  No concessions found for this filter.
                </td></tr>
              )}
              {!loading && items.map((r) => (
                <tr key={r.student_id} data-testid={`row-${r.student_id}`} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2 text-xs text-slate-600">{r.admission_no}</td>
                  <td className="px-3 py-2 font-medium text-slate-900">{r.student_name}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.class_name}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {r.parent_name && <div>{r.parent_name}</div>}
                    {r.parent_phone && <div className="text-slate-400">{r.parent_phone}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 uppercase tracking-wider font-semibold">
                      {r.reason_code}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-xs">{formatINR(r.annual_base)}</td>
                  <td className="px-3 py-2 text-right text-xs">{formatINR(r.annual_effective)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-amber-700">{formatINR(r.annual_waiver)}</td>
                </tr>
              ))}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-slate-50 text-xs font-semibold text-slate-700">
                <tr>
                  <td colSpan={5} className="px-3 py-2 text-right">Total ({items.length} students)</td>
                  <td className="px-3 py-2 text-right">{formatINR(items.reduce((a, r) => a + r.annual_base, 0))}</td>
                  <td className="px-3 py-2 text-right">{formatINR(items.reduce((a, r) => a + r.annual_effective, 0))}</td>
                  <td className="px-3 py-2 text-right text-amber-700">{formatINR(totals.annual_waiver)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  const cls = tone === "amber"
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`border rounded-md px-3 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">{label}</div>
      <div className="text-lg font-semibold mt-1">{value}</div>
    </div>
  );
}
